import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { theme } from '../../styles/theme';
import {
  WebsiteSectionContainer,
  WebsiteMarketingCtaButton,
} from '../common/websiteSection';
import PricingSection from '../pricing/PricingSection';
import PackageComparisonChart from '../account-setup/PackageComparisonChart';
import { featureFlags } from '../../config/featureFlags';
import overviewPreview from '../hero/images/overviewpreview.png';
import calendarPreview from '../hero/images/calendarpreview.png';
import overviewPreviewMobile from '../hero/images/overviewpreviewmobile.png';
import cpPreview1 from '../hero/images/cp-preview1.png';
import salonImage from '../../assets/images/places/salon.jpg';
import spaImage from '../../assets/images/places/spa.jpg';
import fitnessImage from '../../assets/images/places/fitness.jpg';
import homeServicesImage from '../../assets/images/places/homeservices.jpg';
import MarketingHero from './hero/MarketingHero';
import PreviewClickableImage from './hero/PreviewClickableImage';
import HeroPreviewLightbox from './hero/HeroPreviewLightbox';
import StorefrontMarketingSection from '../storefront/StorefrontMarketingSection';

const flatCtaStyles = `
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  background: var(--login-primary);

  &:hover:not(:disabled) {
    transform: none;
    box-shadow: none;
    background: var(--login-primary-dark);
    text-decoration: none;
  }
`;

const RoundCta = styled(WebsiteMarketingCtaButton)`
  border-radius: 999px;
  padding: 14px 28px;
  ${flatCtaStyles}
`;

const FeatureCtaWrap = styled.div`
  width: 100%;

  @media (max-width: 900px) {
    display: flex;
    justify-content: center;
  }
`;

const GhostPillLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 28px;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  color: var(--login-form-text);
  border: 1.5px solid var(--login-form-border);
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: var(--login-primary);
    color: var(--login-primary);
  }
`;

const supportPillStyles = `
  color: #fff;
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.08);

  &:hover {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.14);
  }
`;

const SupportGhostPillLink = styled(GhostPillLink)`
  ${supportPillStyles}
`;

const IndustrySection = styled.section`
  padding: 0 ${theme.spacing.xl} ${theme.spacing['3xl']};
  background: #ffffff;
`;

const SectionHeading = styled.h2`
  margin: 0 0 ${theme.spacing.xl};
  text-align: center;
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--login-form-text);
`;

const IndustryTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xl};
`;

const IndustryTab = styled.button`
  padding: 12px 24px;
  border-radius: 999px;
  border: 1.5px solid ${(props) => (props.$active ? 'var(--login-primary)' : 'var(--login-form-border)')};
  background: ${(props) => (props.$active ? 'rgba(72, 138, 236, 0.08)' : '#fff')};
  color: ${(props) => (props.$active ? 'var(--login-primary)' : 'var(--login-form-muted)')};
  font-family: inherit;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--login-primary);
    color: var(--login-primary);
  }
`;

const IndustryPanel = styled.div`
  max-width: ${(props) => (props.$hasImage ? '1000px' : '900px')};
  margin: 0 auto;
  text-align: ${(props) => (props.$hasImage ? 'left' : 'center')};
  padding: ${(props) => (props.$hasImage ? 0 : theme.spacing['2xl'])};
  border-radius: ${(props) => (props.$hasImage ? 0 : '24px')};
  background: ${(props) => (props.$hasImage ? 'transparent' : 'var(--login-form-input-bg)')};
  border: ${(props) => (props.$hasImage ? 'none' : '1px solid var(--login-form-border)')};

  ${(props) =>
    props.$hasImage &&
    `
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    gap: ${theme.spacing.xl};
    align-items: center;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      text-align: center;
    }
  `}

  h3 {
    margin: 0 0 ${theme.spacing.sm};
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--login-form-text);
  }

  p {
    margin: 0;
    color: var(--login-form-muted);
    line-height: 1.65;
    font-size: ${(props) => (props.$hasImage ? theme.typography.fontSize.lg : theme.typography.fontSize.base)};
    max-width: ${(props) => (props.$hasImage ? 'none' : '52ch')};
    margin-inline: ${(props) => (props.$hasImage ? '0' : 'auto')};
  }
`;

const IndustryPhotoFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  background: var(--login-form-input-bg);
`;

const IndustryPhoto = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 0.45s ease;
  pointer-events: none;
`;

const IndustryPanelCopy = styled.div`
  padding: ${theme.spacing.sm} 0;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const FeatureBlock = styled.section`
  padding: clamp(3rem, 7vw, 5rem) ${theme.spacing.xl};
  background: ${(props) => (props.$alt ? 'var(--login-form-input-bg)' : '#ffffff')};
`;

const FeatureRow = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(2rem, 5vw, 4rem);
  align-items: center;

  ${(props) =>
    props.$reverse &&
    `
    direction: rtl;
    > * { direction: ltr; }
  `}

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    direction: ltr;

    > * {
      direction: ltr;
    }
  }
`;

const FeatureCopy = styled.div`
  h2 {
    margin: 0 0 ${theme.spacing.md};
    font-size: clamp(1.75rem, 3vw, 2.5rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.15;
    color: var(--login-form-text);
  }

  > p {
    margin: 0 0 ${theme.spacing.lg};
    color: var(--login-form-muted);
    line-height: 1.65;
    font-size: ${theme.typography.fontSize.lg};
  }
`;

const BulletList = styled.ul`
  margin: 0 0 ${theme.spacing.xl};
  padding: 0;
  list-style: none;

  li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: ${theme.spacing.md};
    color: var(--login-form-muted);
    line-height: 1.55;
    font-size: ${theme.typography.fontSize.base};

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.6em;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--login-primary);
    }

    strong {
      color: var(--login-form-text);
      font-weight: 600;
    }
  }
`;

const FeatureImage = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.12);
  border: 1px solid var(--login-form-border);

  img {
    display: block;
    width: 100%;
  }
`;

const FeaturesHub = styled.section`
  padding: clamp(3rem, 7vw, 5rem) ${theme.spacing.xl};
  background: #ffffff;
  scroll-margin-top: 100px;
`;

const CategoryTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing['2xl']};
`;

const CategoryTab = styled.button`
  padding: 10px 20px;
  border: none;
  border-bottom: 2px solid ${(props) => (props.$active ? 'var(--login-primary)' : 'transparent')};
  background: none;
  color: ${(props) => (props.$active ? 'var(--login-form-text)' : 'var(--login-form-muted)')};
  font-family: inherit;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${(props) => (props.$active ? '700' : '500')};
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--login-form-text);
  }
`;

const FeatureLinkGrid = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
  align-items: start;
`;

const FeatureLink = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: 12px;
  background: var(--login-form-input-bg);
  border: 1px solid var(--login-form-border);
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  line-height: 1.35;
  color: var(--login-form-text);
`;

const Showcase = styled.section`
  padding: clamp(3rem, 7vw, 5rem) ${theme.spacing.xl};
  background: linear-gradient(180deg, var(--login-form-input-bg) 0%, #ffffff 100%);
  text-align: center;
`;

const ShowcaseTitle = styled.h2`
  margin: 0 0 ${theme.spacing.sm};
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
`;

const ShowcaseSubtitle = styled.p`
  margin: 0 0 ${theme.spacing.xl};
  color: var(--login-form-muted);
  font-size: ${theme.typography.fontSize.lg};
`;

const VideoFrame = styled.div`
  max-width: min(340px, 78vw);
  margin: 0 auto ${theme.spacing['2xl']};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.12);
  border: 1px solid var(--login-form-border);
  position: relative;
  background: #fff;

  img {
    display: block;
    width: 100%;
    height: auto;
    opacity: 1;
  }
`;

const TrustRow = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;

  h3 {
    margin: 0 0 ${theme.spacing.xl};
    font-size: ${theme.typography.fontSize.xl};
    font-weight: 700;
    color: var(--login-form-muted);
    letter-spacing: -0.01em;
  }
`;

const LogoStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${theme.spacing.xl};
  opacity: 0.55;

  span {
    font-size: ${theme.typography.fontSize.lg};
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--login-form-muted);
  }
`;

const TestimonialGrid = styled.div`
  max-width: 1140px;
  margin: ${theme.spacing['3xl']} auto 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled.blockquote`
  margin: 0;
  padding: ${theme.spacing.xl};
  border-radius: 20px;
  background: #fff;
  border: 1px solid var(--login-form-border);
  text-align: left;
  box-shadow: var(--login-form-shadow);

  p {
    margin: 0 0 ${theme.spacing.lg};
    color: var(--login-form-muted);
    line-height: 1.65;
    font-size: ${theme.typography.fontSize.base};
  }

  footer {
    strong {
      display: block;
      font-size: ${theme.typography.fontSize.sm};
      color: var(--login-form-text);
    }

    span {
      font-size: ${theme.typography.fontSize.sm};
      color: var(--login-form-muted);
    }
  }
`;

const SupportSection = styled.section`
  padding: clamp(3rem, 7vw, 5rem) ${theme.spacing.xl};
  background: var(--login-brand-bg0);
  color: #fff;
  text-align: center;
  scroll-margin-top: 100px;
`;

const SupportInner = styled.div`
  max-width: 640px;
  margin: 0 auto;

  h2 {
    margin: 0 0 ${theme.spacing.md};
    font-size: clamp(1.75rem, 3vw, 2.5rem);
    font-weight: 800;
    letter-spacing: -0.03em;
  }

  p {
    margin: 0 0 ${theme.spacing.xl};
    color: rgba(255, 255, 255, 0.72);
    line-height: 1.65;
    font-size: ${theme.typography.fontSize.lg};
  }

  a.contact-link {
    display: inline-block;
    margin-bottom: ${theme.spacing.lg};
    font-size: clamp(1.125rem, 2vw, 1.375rem);
    font-weight: 600;
    color: #fff;
    text-decoration: none;

    &:hover {
      color: var(--login-primary-light);
    }
  }

  small {
    display: block;
    margin-bottom: ${theme.spacing.xl};
    color: rgba(255, 255, 255, 0.5);
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const ComparisonSection = styled.section`
  padding: clamp(3rem, 7vw, 5rem) ${theme.spacing.xl};
  background: var(--login-form-input-bg);
  scroll-margin-top: 100px;
`;

const ComparisonIntro = styled.p`
  text-align: center;
  color: var(--login-form-muted);
  font-size: ${theme.typography.fontSize.lg};
  line-height: 1.6;
  max-width: 640px;
  margin: 0 auto ${theme.spacing['2xl']};
`;

const INDUSTRIES = [
  {
    id: 'salons',
    label: 'Salons',
    title: 'Salons',
    copy: 'Keep stylists booked, manage client preferences, and let customers self-book online 24/7.',
    image: salonImage,
    imageAlt: 'Modern hair salon interior with styling stations',
  },
  {
    id: 'spas',
    label: 'Spas',
    title: 'Spas & Wellness',
    copy: 'Coordinate rooms, therapists, and packages — with intake forms and memberships built in.',
    image: spaImage,
    imageAlt: 'Serene spa treatment room with massage table and wellness products',
  },
  {
    id: 'fitness',
    label: 'Fitness',
    title: 'Fitness Studios',
    copy: 'Class schedules, trainer availability, and recurring memberships without spreadsheet chaos.',
    image: fitnessImage,
    imageAlt: 'Group fitness class in a bright studio with yoga mats',
  },
  {
    id: 'services',
    label: 'Home Services',
    title: 'Home & Field Services',
    copy: 'Dispatch teams, manage travel buffers, and give customers real-time booking from any device.',
    image: homeServicesImage,
    imageAlt: 'Home service team arriving at a residence with cleaning equipment',
  },
];

const FEATURE_CATEGORIES = {
  client: {
    label: 'Client Experience',
    items: [
      'Branded Storefront',
      'Online Booking',
      'Client Portal',
      'Custom Pages',
      'Reminders',
      'Client Profiles',
      'Self-Service Booking',
    ],
  },
  operations: {
    label: 'Operations',
    items: ['Scheduling', 'Staff Management', 'Calendar Sync', 'Employee Permissions', 'Point of Sale', 'Reports'],
  },
  growth: {
    label: 'Business Growth',
    items: ['Email Reminders', 'SMS Notifications', 'Promotions', 'Analytics', 'Payments', 'Support Tickets'],
  },
};

const ALTERNATING_BLOCKS = [
  {
    title: 'Streamline bookings, boost productivity',
    intro: 'Simplify scheduling with smart tools that keep your team running smoothly.',
    bullets: [
      { lead: 'Online booking, 24/7:', text: 'Let clients book anytime with an intuitive flow that keeps your calendar full.' },
      { lead: 'Smart staff scheduling:', text: 'Assign services, manage availability, and avoid double-booking conflicts.' },
      { lead: 'Fewer no-shows:', text: 'Automated reminders, deposits, and waitlist tools protect your revenue.' },
    ],
    image: calendarPreview,
    alt: 'Neat Schedule calendar and scheduling view',
    enlargeable: true,
    lightboxLabel: 'Full-size calendar preview',
  },
  {
    title: 'Make data-driven decisions with ease',
    intro: 'Stay ahead with real-time insights from your Neat Schedule dashboard.',
    bullets: [
      { lead: 'Stay in control:', text: 'Overview trends, bookings, staff productivity, and rebooking rates at a glance.' },
      { lead: 'Identify growth:', text: 'Pinpoint top-performing services and where to improve.' },
      { lead: 'Clear reporting:', text: 'Actionable data — no guesswork required.' },
    ],
    image: overviewPreview,
    alt: 'Neat Schedule dashboard overview',
    reverse: true,
  },
  {
    title: 'Payments and clients, simplified',
    intro: 'Everything your front desk and clients need in one connected experience.',
    bullets: [
      { lead: 'Flexible payments:', text: 'Accept cards, deposits, and tips through trusted processors.' },
      { lead: 'Client portal:', text: 'Let customers manage appointments, history, and preferences themselves.' },
      { lead: 'Unified profiles:', text: 'Notes, preferences, and visit history travel with every booking.' },
    ],
    image: cpPreview1,
    alt: 'Neat Schedule client portal booking flow',
  },
];

const TRUSTED_BY_EXAMPLES = [
  'Maple Street Salon',
  'Harbor Wellness Spa',
  'Ironclad Fitness',
  'BrightPath Home Services',
  'Cornerstone Barbers',
];

const TESTIMONIALS = [
  {
    quote: 'We cut no-shows dramatically and our front desk finally has one system instead of five.',
    name: 'Maria Gonzalez',
    business: 'Maple Street Salon',
  },
  {
    quote: 'Setup was straightforward. Our trainers were booking classes online within the first week.',
    name: 'Derek Thompson',
    business: 'Ironclad Fitness',
  },
  {
    quote: 'Dispatching crews and letting clients book online saved us hours every week.',
    name: 'Lisa Nguyen',
    business: 'BrightPath Home Services',
  },
];

function DemoButton({ children, className, style, onDark = false }) {
  const PillLink = onDark ? SupportGhostPillLink : GhostPillLink;

  return (
    <PillLink to="/book-demo" className={className} style={style}>
      {children}
    </PillLink>
  );
}

const INDUSTRY_ROTATE_MS = 4000;
const INDUSTRY_RESUME_MS = 20000;

export default function MarketingHome() {
  const [industry, setIndustry] = useState('salons');
  const [industryPausedUntil, setIndustryPausedUntil] = useState(0);
  const [featureTab, setFeatureTab] = useState('client');
  const [previewLightbox, setPreviewLightbox] = useState(null);
  const trialEnabled = featureFlags.FREE_TRIAL_ENABLED;
  const heroCtaLabel = trialEnabled ? 'Get Started for Free' : 'Get Started';
  const sectionCtaLabel = trialEnabled ? 'Start a Free Trial' : 'Get Started';

  const activeIndustry = INDUSTRIES.find((i) => i.id === industry) || INDUSTRIES[0];
  const activeFeatures = FEATURE_CATEGORIES[featureTab];

  useEffect(() => {
    INDUSTRIES.forEach((item) => {
      if (!item.image) return;
      const img = new Image();
      img.src = item.image;
    });
  }, []);

  useEffect(() => {
    let rotateTimer = null;
    let resumeTimer = null;
    const remainingPause = industryPausedUntil - Date.now();

    if (remainingPause > 0) {
      resumeTimer = window.setTimeout(() => {
        setIndustryPausedUntil(0);
      }, remainingPause);
      return () => window.clearTimeout(resumeTimer);
    }

    rotateTimer = window.setInterval(() => {
      setIndustry((current) => {
        const index = INDUSTRIES.findIndex((item) => item.id === current);
        const nextIndex = (Math.max(index, 0) + 1) % INDUSTRIES.length;
        return INDUSTRIES[nextIndex].id;
      });
    }, INDUSTRY_ROTATE_MS);

    return () => {
      if (rotateTimer) window.clearInterval(rotateTimer);
      if (resumeTimer) window.clearTimeout(resumeTimer);
    };
  }, [industryPausedUntil]);

  const selectIndustry = (id) => {
    setIndustry(id);
    setIndustryPausedUntil(Date.now() + INDUSTRY_RESUME_MS);
  };

  return (
    <>
      <MarketingHero
        heroCtaLabel={heroCtaLabel}
        trialEnabled={trialEnabled}
      />

      <IndustrySection>
        <SectionHeading>Built for the way you work</SectionHeading>
        <IndustryTabs>
          {INDUSTRIES.map((item) => (
            <IndustryTab
              key={item.id}
              type="button"
              $active={industry === item.id}
              aria-pressed={industry === item.id}
              onClick={() => selectIndustry(item.id)}
            >
              {item.label}
            </IndustryTab>
          ))}
        </IndustryTabs>
        <IndustryPanel $hasImage={Boolean(activeIndustry.image)}>
          {activeIndustry.image ? (
            <>
              <IndustryPhotoFrame>
                {INDUSTRIES.map((item) =>
                  item.image ? (
                    <IndustryPhoto
                      key={item.id}
                      src={item.image}
                      alt={industry === item.id ? item.imageAlt || item.title : ''}
                      $visible={industry === item.id}
                      loading="eager"
                      decoding="async"
                      aria-hidden={industry !== item.id}
                    />
                  ) : null
                )}
              </IndustryPhotoFrame>
              <IndustryPanelCopy>
                <h3>{activeIndustry.title}</h3>
                <p>{activeIndustry.copy}</p>
              </IndustryPanelCopy>
            </>
          ) : (
            <>
              <h3>{activeIndustry.title}</h3>
              <p>{activeIndustry.copy}</p>
            </>
          )}
        </IndustryPanel>
      </IndustrySection>

      <StorefrontMarketingSection
        sectionCtaLabel={sectionCtaLabel}
        onPreviewClick={setPreviewLightbox}
      />

      {ALTERNATING_BLOCKS.map((block) => (
        <FeatureBlock key={block.title} $alt={block.reverse}>
          <FeatureRow $reverse={block.reverse}>
            <FeatureCopy>
              <h2>{block.title}</h2>
              <p>{block.intro}</p>
              <BulletList>
                {block.bullets.map((b) => (
                  <li key={b.lead}>
                    <strong>{b.lead}</strong> {b.text}
                  </li>
                ))}
              </BulletList>
              <FeatureCtaWrap>
                <RoundCta as={Link} to="/main/account-setup">
                  {sectionCtaLabel}
                </RoundCta>
              </FeatureCtaWrap>
            </FeatureCopy>
            <FeatureImage>
              {block.enlargeable ? (
                <PreviewClickableImage
                  src={block.image}
                  alt={block.alt}
                  ariaLabel={`View full-size ${block.alt}`}
                  onClick={() =>
                    setPreviewLightbox({
                      src: block.image,
                      alt: block.alt,
                      dialogAriaLabel: block.lightboxLabel || 'Full-size app preview',
                    })
                  }
                />
              ) : (
                <img src={block.image} alt={block.alt} />
              )}
            </FeatureImage>
          </FeatureRow>
        </FeatureBlock>
      ))}

      <FeaturesHub id="features">
        <WebsiteSectionContainer>
          <SectionHeading>Features designed to keep you booked</SectionHeading>
          <CategoryTabs>
            {Object.entries(FEATURE_CATEGORIES).map(([key, cat]) => (
              <CategoryTab
                key={key}
                type="button"
                $active={featureTab === key}
                onClick={() => setFeatureTab(key)}
              >
                {cat.label}
              </CategoryTab>
            ))}
          </CategoryTabs>
          <FeatureLinkGrid>
            {activeFeatures.items.map((item) => (
              <FeatureLink key={item}>{item}</FeatureLink>
            ))}
          </FeatureLinkGrid>
        </WebsiteSectionContainer>
      </FeaturesHub>

      <Showcase>
        <ShowcaseTitle>Beautiful. Simple. Powerful.</ShowcaseTitle>
        <ShowcaseSubtitle>See how Neat Schedule keeps your team organized and your clients happy.</ShowcaseSubtitle>
        <VideoFrame>
          <PreviewClickableImage
            src={overviewPreviewMobile}
            alt="Neat Schedule mobile dashboard overview"
            ariaLabel="View full-size Neat Schedule mobile dashboard preview"
            onClick={() =>
              setPreviewLightbox({
                src: overviewPreviewMobile,
                alt: 'Neat Schedule mobile dashboard overview',
                dialogAriaLabel: 'Full-size mobile dashboard preview',
              })
            }
          />
        </VideoFrame>

        <TrustRow>
          <h3>Trusted by growing service businesses</h3>
          <LogoStrip>
            {TRUSTED_BY_EXAMPLES.map((name) => (
              <span key={name}>{name}</span>
            ))}
          </LogoStrip>
        </TrustRow>

        <TestimonialGrid>
          {TESTIMONIALS.map((t) => (
            <TestimonialCard key={t.name}>
              <p>&ldquo;{t.quote}&rdquo;</p>
              <footer>
                <strong>{t.name}</strong>
                <span>{t.business}</span>
              </footer>
            </TestimonialCard>
          ))}
        </TestimonialGrid>
      </Showcase>

      <SupportSection id="support">
        <SupportInner>
          <h2>Human support. Always.</h2>
          <p>
            Real people help you get set up and stay successful. Reach out when you need
            answers — we are here to help.
          </p>
          <Link className="contact-link" to="/contact">
            contact@neatschedule.com
          </Link>
          <small>Monday to Friday · 9 AM to 6 PM EST</small>
          <DemoButton onDark>
            Book a Demo
          </DemoButton>
        </SupportInner>
      </SupportSection>

      <PricingSection />

      <ComparisonSection id="comparison">
        <WebsiteSectionContainer>
          <SectionHeading>Compare plans in detail</SectionHeading>
          <ComparisonIntro>
            Find the perfect plan for your business by comparing features and capabilities across every package.
          </ComparisonIntro>
          <PackageComparisonChart />
        </WebsiteSectionContainer>
      </ComparisonSection>

      <HeroPreviewLightbox
        open={Boolean(previewLightbox)}
        imageSrc={previewLightbox?.src}
        imageAlt={previewLightbox?.alt}
        dialogAriaLabel={previewLightbox?.dialogAriaLabel}
        onClose={() => setPreviewLightbox(null)}
      />
    </>
  );
}
